// Sculk vibrations as a world event (M22.1 extension). PURE (ENG-1).
//
// Vibrations already existed as a concept — sculk_sensor.ts owns the game-event →
// frequency table a calibrated sensor filters on — but only ONE thing in the world
// ever emitted one: the player moving. Nothing a mob did, and no block edit, was
// heard by anything.
//
// This module is the routing layer that makes a vibration a real world event: who
// can hear it, and how much it angers a listening warden. The warden's anger ledger
// (warden.ts) is what turns that into a hunt — and because a vibration carries the
// id of whatever caused it, the warden can end up hunting a mob rather than the
// player, which is the whole point of a blind hunter.
import { WARDEN } from '../config';
import { vibrationFrequency } from './sculk_sensor';

/** The source id carried by a vibration the player caused. Mobs use their entity
 *  id (always ≥ 1), so this can never collide with one. */
export const PLAYER_SOURCE = 0;

/** One emitted vibration: what happened, where, and who caused it. */
export interface Vibration {
  event: string;
  x: number;
  y: number;
  z: number;
  /** Entity id of the culprit, or PLAYER_SOURCE. */
  source: number;
}

/** Does a listener at `dist` blocks hear this vibration? */
export function vibrationReaches(dist: number): boolean {
  return dist <= WARDEN.VIBRATION_RANGE;
}

/**
 * Anger a vibration adds to a warden's ledger. Louder events anger more: the
 * frequency table already ranks game events roughly by how disruptive they are
 * (a step is 1, breaking a block 11, an explosion 13), so anger is derived from
 * it rather than duplicating a second table that could drift out of step.
 */
export function vibrationAnger(event: string): number {
  const freq = vibrationFrequency(event);
  if (freq <= 0) return WARDEN.ANGER_PER_VIBRATION; // unknown event: a quiet default
  return WARDEN.ANGER_PER_VIBRATION + Math.round(freq * WARDEN.ANGER_PER_FREQUENCY);
}

/** Should a warden bother tracking this culprit at all? A warden ignores its own
 *  noise (and any other warden's), so a pair of them never hunt each other. */
export function angersWarden(sourceType: string | null): boolean {
  return sourceType !== 'warden';
}
